import { OrgPart, Part, PartQuoteCreateUpdate } from 'types/part';
import { Org, OrgAddressBase, OrgLite } from 'types/org';
import { User, UserLite } from 'types/user';
import { ColorOptions, DateString, DateTimeString } from 'types';
import { ProductionRun, ProductionRunLite } from 'types/production';
import { HandlingRequestCostItem, ShipmentCostItem } from 'types/purchasing';
import { PartRuleSet } from 'types/partRules';
import { GenericResource, Lite } from 'types/api';
import { OrgSupplier } from 'types/suppliers';
import { Team, TeamLite } from 'types/properties';
import { CreatePurchaseEventRequest, OrderUpdateStatus } from 'types/order';
import { CreateUpdatePurchaseOrder } from 'types/procurement';
import {
  KitRequestLineSchemaLite,
  KitRequestSchema,
  KitRequestSchemaLite,
} from 'types/kitting';
import {
  Icon,
  IconArrowsJoin,
  IconBox,
  IconBuildingStore,
  IconCalendarEvent,
  IconCamera,
  IconCircuitResistor,
  IconCpu,
  IconCut,
  IconDisc,
  IconDropletOff,
  IconFile,
  IconFile3d,
  IconFileAlert,
  IconFileAnalytics,
  IconFileBarcode,
  IconFileCertificate,
  IconFileCheck,
  IconFileDelta,
  IconFileDollar,
  IconFileInfo,
  IconFilePencil,
  IconFileSpreadsheet,
  IconFileTypeJpg,
  IconFileZip,
  IconMicroscope,
  IconNote,
  IconPlaneDeparture,
  IconPlus,
  IconRadioactive,
  IconRulerMeasure,
  IconTallymarks,
  IconTransferIn,
  IconTrash,
  IconTruckDelivery,
} from '@tabler/icons-react';
import {
  OrgPartSchema,
  PART_VERSION,
  V2OrgPartQueryAndClientV2Types,
  V2OrgPartTypes,
} from 'types/clientV2/parts';
import { StockLocationPurpose } from 'types/clientV2/stockLocations';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

// USE with stock_lots endpoints
export type StockLotCRUDSchema = GenericResource & {
  id: string;
  org: Org;
  status: string;
  lotId: string;
  package: PackagingOptions;
  quantCertain: boolean;
  quarantined: boolean;
  pending: boolean;
  quant: number;
  ownedQuant: number;
  part: Part;
  printedPart: string;
  unitPrice: string;
  landedUnitPrice: string;
  allowedRequests: string[];
  createdAt: DateString;
  expiresAt?: DateString | null;
  tapeSize: number | null;
  mfgLot: string | null;
  countryOfOrigin: string | null;
  dateCode: string | null;
  internalNotes: string | null;
  externalNotes: string | null;
  notes: string | null;
  msl: string | null;
  locationName: string | null;
  mslFloorHours: number | null;
  stockLocation: StockLocation | null;
  facility: StockLocationLite | null;
  expectedDate: DateString | null;
  bonded: DateString | null;
  handlingRequests: HandlingRequestLite[];
  shipments: ShipmentPickingIcon[];
  auditedAt: DateTimeString | null;
  dataSourceSyncedAt: DateTimeString | null;
  dataSource: DataSource | null;
  purchaseLineQuant: number;
  purchaseLine: string | null;
  receivedFromIngest: boolean;
  smtNoPnp: boolean;
  team?: TeamLite;
  customProperties?: object;
  createdFrom?: string | null;
  partVersion?: PART_VERSION;
  dimensionClass: DimensionClassOptions;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionValue: number;
} & Pick<V2OrgPartTypes, 'v2OrgPart'>;

export type StockLotLite = Lite<StockLotCRUDSchema>;

export type StockLotSchemaClientV2 = {
  id: string;
  ownedQuant: number;
  notes: string | null;
  status: string;
  countryOfOrigin: string | null;
  createdAt: DateString | null;
  updatedAt: DateString | null;
  internalMetadata: object;
  externalMetadata: object;
  customProperties?: object;
  extDataSourceId: string | null;
  externallySynced: boolean;
  part: Part;
  printedPart: string;
  lotId: string;
  quant: number;
  dateCode: string | null;
  mfgLot: string | null;
  quantCertain: boolean;
  quarantined: boolean;
  smtNoPnp: boolean;
  pending: boolean;
  msl: string | null;
  mslFloorHours: number | null;
  tapeSize: number;
  bonded: string | null;
  auditedAt: string | null;
  expiresAt: string | null;
  dataSource: string | null;
  dataSourceSyncedAt: string | null;
  expectedBy: string | null;
  package: PackagingOptions;
  orgPart: string;
  team: Team['id'] | null;
  stockLocation: StockLocation;
  facility: StockLocationLite;
  purchaseLine: string | null;
  createdFrom: string | null;
  replacedBy: string | null;
  org: Org;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionValue: number;
  dimensionClass: DimensionClassOptions;
};

// USE with query_stock_lots
export type StockLotQuerySchema = {
  id: string;
  /** --------------------------------- */
  /** --- Deprecated Part V1 Fields --- */
  /** --------------------------------- */
  part: Part;
  orgPart: OrgPart;
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  partVersion: PART_VERSION;
  v2OrgPart: V2OrgPartQueryAndClientV2Types['v2OrgPart'];
  /** ------------------------ */
  /** --- Remaining Fields --- */
  /** ------------------------ */
  org: Org;
  status: string;
  lotId: string;
  mfgLot: string | null;
  ownedQuant: number;
  notes: string | null;
  countryOfOrigin: string | null;
  createdFrom: string | null;
  replacedBy: string | null;
  stockLocation: StockLocation;
  facility: StockLocationLite;
  purchaseLine: string | null;
  handlingRequests: HandlingRequestLite[];
  shipments: Shipment[];
  locationDisplay: string;
  partLotId: string;
  createdDate: string;
  expiresAt: string | null;
  mslHoursLeft: number;
  unitPrice: string;
  landedUnitPrice: string;
  expectedDate: DateString | null;
  createdAt: DateString;
  updatedAt: DateString;
  printedPart: string;
  quant: number;
  dateCode: string | null;
  quantCertain: boolean;
  quarantined: boolean;
  smtNoPnp: boolean;
  pending: boolean;
  msl: string | null;
  mslFloorHours: number | null;
  tapeSize: number | null;
  bonded: string | null;
  auditedAt: DateString | null;
  dataSource: DataSource | null;
  dataSourceSyncedAt: DateTimeString | null;
  expectedBy: string | null;
  package: PackagingOptions;
  team: Team['id'] | null;
  height: number | null;
  width: number | null;
  length: number | null;
  weight: number | null;
  customProperties?: object;
  deletedAt: DateTimeString | null;
  dimensionClass: DimensionClassOptions;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionValue: number;
};

export type StockLotQuerySchemaLite = Lite<StockLotQuerySchema>;

export type StockLotWithPrice = StockLotCRUDSchema & {
  unitPrice?: number | null;
  landedUnitPrice?: number | null;
};

export type ConsumeStockEvent = {
  id: string;
  stockLot: StockLotWithPrice;
  quant: number;
};

export type ShipmentBase = {
  id: string;
  status: ShipmentStatus;
  trackingNumber: string;
  carrier: string;
  service: string;
  shipmentCode: string;
  notes: string | null;
  notificationEmail: string | null;
  billable: boolean;
  shipPartial: boolean;
  deadline: DateString | null;
  approvedAt?: DateString | null;
  shippedAt: DateString | null;
  estimatedDelivery: DateString | null;
  receivedAt: DateString | null;
  approved: boolean;
  locked: boolean;
  address: OrgAddressBase;
  org: string;
  shipFrom: StockLocation | null;
  shipTo: StockLocation | null;
  shippedBy: string | null;
  receivedBy: string | null;
  parentShipment: string | null;
  childShipments: string[];
  productionRun: ProductionRunLite | null;
  hierarchy?: string[];
  shipmentType: ShipmentType;
  createdAt?: DateTimeString;
  internalNotes?: string | null;
  team?: TeamLite;
  customProperties?: object;
  ingestedAt?: DateTimeString | null;
};

export type Shipment = ShipmentBase & {
  lines: ShipmentLine[];
  org: Org;
  relatedRecords?: RelatedRecord[];
  approvedBy: User | null;
  orderExternalPoNumber?: string | null;
};

export type ShipmentLite = Omit<ShipmentBase, 'team'> & {
  lines: ShipmentLine['id'][];
  approvedBy: string | null;
  team?: Team['id'];
};

export type ShipmentPickingIcon = {
  id: string;
  shipmentCode: string;
  approvedAt: DateString | null;
  shippedAt: DateString | null;
  receivedAt: DateString | null;
};

export type ExpandedShipments = ShipmentBase & {
  lines: ShipmentLineLiteExpandedStockLot[];
  relatedRecords?: RelatedRecord[];
  approvedBy: User | null;
};

export enum ShipmentLineStatus {
  TO_DO = 'to_do',
  PICKED = 'picked',
  PACKED = 'packed',
  INGESTED = 'ingested',
}

export type ShipmentLine = {
  id: string;
  status: ShipmentLineStatus | null;
  notes: string | null;
  printedNotes: string | null;
  shipment: string;
  stockLot: StockLotWithPrice;
  shippedQuant: number | null;
  quantAfterApproval?: number;
  packageAfterApproval?: string;
  pickedAt?: DateTimeString | null;
  customProperties?: object;
  ingestedAt?: DateTimeString | null;
};

export type ShipmentLineLiteExpandedStockLot = {
  id: string;
  status: string | null;
  notes: string | null;
  printedNotes: string | null;
  shipment: string;
  stockLot: StockLotLite;
  shippedQuant: number | null;
  quantAfterApproval?: number;
  packageAfterApproval?: string;
};

export type ShipmentLineLite = {
  id: string;
  status: string | null;
  notes: string | null;
  printedNotes: string | null;
  shipment: string;
  stockLot: string;
  shippedQuant: number | null;
  quantAfterApproval?: number;
  packageAfterApproval?: string;
  customProperties?: object;
};

export type ShipmentLineUpdateRequest = {
  status?: string;
  notes?: string | null;
  printedNotes?: string | null;
  stockLocation?: StockLocation['id'] | null;
  stockLot?: string;
  transferRequests?: boolean;
};

export type ShipmentEstimatesRequest = {
  shipments: {
    shipmentId: Shipment['id'];
  }[];
  handlingRequests?: {
    requestType: string;
    stockLot?: StockLotCRUDSchema['id'];
  }[];
};

export type ShipmentEstimates = {
  shippingCosts: ShipmentCostItem[];
  shippingTotal: number;
  handlingRequestsCosts: HandlingRequestCostItem[];
  handlingRequestsTotal: number;
  packingTotal: number;
  totalCost: number;
  shippingPartial: boolean;
};

export type ShipmentConflicts = {
  canShipAll: boolean;
  shipments: ShipmentConflict[];
};

export type ShipmentConflict = {
  id: Shipment['id'];
  canShip: boolean;
  deadline?: DateString;
  conflictedLines: ShipmentConflictedLine[];
};

export type ShipmentConflictedLine = {
  id: ShipmentLine['id'];
  reasons: ConflictReason[];
  requestedQuant: number;
  status: string;
  stockLot: StockLotCRUDSchema;
};

export type ConflictReason = {
  status: string;
  msg: string;
  conflictedHandlingRequest?: HandlingRequest;
  conflictedShipmentLine?: ShipmentLineCausingConflicts;
};

export type ShipmentLineCausingConflicts = {
  id: ShipmentLine['id'];
  status: string;
  notes: string | null;
  printedNotes: string | null;
  shippedQuant: number | null;
  shipment: Shipment['id'];
  stockLot: StockLotCRUDSchema['id'];
};

export type Ledger = {
  ledgerType: string;
  date: DateString;
  expected: number | null;
  onHand: number | null;
  allocated: number | null;
  price: number | null;
  stockLocation: StockLocation | null;
  notes: string | null;
  relatedRecords: RelatedRecord[];
  performedBy: UserLite;
};

export enum StockLocationType {
  FACILITY = 'facility',
  ESD_BIN = 'esd_bin_201506',
  BIN_SECTION = 'bin_section',
  BIG_REEL_HOPPER = 'big_reel_hopper',
  BAG = 'bag',
  WORKSTATION = 'workstation',
  DRY_CABINET = 'dry_cabinet',
  SHELF = 'shelf',
  PALLET = 'pallet',
  OTHER = 'other',
  NOT_SPECIFIED = 'not_specified',
}

export const StockLocationTypeLabels: {
  [key in StockLocationType]: string;
} = {
  facility: 'Address',
  esd_bin_201506: 'ESD Bin',
  bin_section: 'Bin Section',
  big_reel_hopper: 'Large Reel Storage',
  bag: 'Bag',
  workstation: 'Workstation',
  dry_cabinet: 'Dry Cabinet',
  shelf: 'Shelf',
  pallet: 'Pallet',
  other: 'Other',
  not_specified: '-',
};

export type StockLocation = {
  id: string;
  name: string;
  stockLocationCode: string;
  externalName: string | null;
  facility: StockLocationLite | null;
  billable: boolean;
  locationType: StockLocationType;
  customerLocation: boolean;
  org: string | null;
  address: OrgAddressBase;
  parent: string | StockLocation | null;
  shipment?: Shipment | null;
  notificationEmail?: string;
  manufacturer: boolean;
  requiresMslReset: boolean;
  requiresLcrInspection: boolean;
  requiresDimensionalInspection: boolean;
  requiresReels: boolean;
  requiresMerges: boolean;
  requiresSplits: boolean;
  consumesStock: boolean;
  childStockLots: StockLotCRUDSchema[];
  stockLots: StockLotCRUDSchema[];
  warehouseShippingEnabled?: boolean;
  warehouseHandlingEnabled?: boolean;
  acceptsSmtNoPnp?: boolean;
  defaultPackage?: PackagingOptions;
  partRuleSet?: PartRuleSet['id'];
  locked?: boolean;
  team?: TeamLite;
  customProperties?: object;
  kitRequest?: KitRequestSchema | null;
  purpose: StockLocationPurpose | null;
  useTeams?: boolean;
};

export type StockLocationLite = {
  id: string;
  address: OrgAddressBase;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  name: string;
  locationType: StockLocationType;
  active: boolean;
  billable: boolean;
  customerLocation: boolean;
  externalName: string | null;
  addressLine_1: string | null;
  addressLine_2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressCountry: string | null;
  addressPostalCode: string | null;
  addressCompany: string | null;
  addressName: string | null;
  addressPhone: string | null;
  parent: string | null;
  org: string | null;
  warehouseShippingEnabled?: boolean;
  warehouseHandlingEnabled?: boolean;
  acceptsSmtNoPnp?: boolean;
  partRuleSet?: PartRuleSet['id'];
  team?: Team['id'];
  stockLocationCode?: string | null;
  useTeams?: boolean;
  requiresReels?: boolean;
};

export enum StockDocumentProcessingStatus {
  RECEIVED = 'received',
  PRE_PROCESSING = 'pre_processing',
  IGNORED_BY_FILTER = 'ignored_by_filter',
  DATAMATRIX_FOUND = 'datamatrix_found',
  CLASSIFICATION = 'classification',
  OCR = 'ocr',
  EXTRACTION_SUMMARY = 'extraction_summary',
  EXTRACTION_CHUNK_PROCESSING = 'extraction_chunk_processing',
  EXTRACTION_ASSEMBLY = 'extraction_assembly',
  EXTRACTION_NORMALIZATION = 'extraction_normalization',
  MATCHING = 'matching',
  SELF_CORRECTION = 'self_correction',
  VALIDATION = 'validation',
  UPDATE_PROCESSING = 'update_processing',
  SUBMITTED = 'submitted',
}

export const StockDocumentProcessingStatusDisplayName: Record<
  StockDocumentProcessingStatus,
  string
> = {
  [StockDocumentProcessingStatus.RECEIVED]: 'Received',
  [StockDocumentProcessingStatus.PRE_PROCESSING]: 'Pre-Processing',
  [StockDocumentProcessingStatus.IGNORED_BY_FILTER]: 'Ignored By Filter',
  [StockDocumentProcessingStatus.DATAMATRIX_FOUND]: 'Datamatrix Found',
  [StockDocumentProcessingStatus.CLASSIFICATION]: 'Classification',
  [StockDocumentProcessingStatus.OCR]: 'OCR',
  [StockDocumentProcessingStatus.EXTRACTION_SUMMARY]: 'Extraction Summary',
  [StockDocumentProcessingStatus.EXTRACTION_CHUNK_PROCESSING]:
    'Extraction Chunk Processing',
  [StockDocumentProcessingStatus.EXTRACTION_ASSEMBLY]: 'Extraction Assembly',
  [StockDocumentProcessingStatus.EXTRACTION_NORMALIZATION]:
    'Extraction Normalization',
  [StockDocumentProcessingStatus.MATCHING]: 'Matching',
  [StockDocumentProcessingStatus.SELF_CORRECTION]: 'Self Correction',
  [StockDocumentProcessingStatus.VALIDATION]: 'Validation',
  [StockDocumentProcessingStatus.UPDATE_PROCESSING]: 'Update Processing',
  [StockDocumentProcessingStatus.SUBMITTED]: 'Submitted',
};

export enum ExtractorFieldNames {
  MPN = 'mpn',
  SKU = 'sku',
  CPID = 'cpid',
  TRACKING_NUMBER = 'trackingNumber',
  COO = 'coo',
  HTSUS = 'htsus',
  ECCN = 'eccn',
  DATE_CODE = 'dateCode',
  MFG_LOT = 'mfgLot',
  DOCUMENT_DATE = 'documentDate',
  SHIP_DATE = 'shipDate',
  DELIVERY_DATE = 'deliveryDate',
  RELEASE_DATE = 'releaseDate',
  HAS_COFC = 'hasCofc',
  QUANT_ORDERED = 'quantOrdered',
  QUANT_SHIPPED = 'quantShipped',
  QUANT_BACKORDERED = 'quantBackordered',
  // eslint-disable-next-line no-restricted-syntax
  QUANT_CANCELLED = 'quantCancelled',
  UNIT_PRICE = 'unitPrice',
  LINE_PRICE = 'linePrice',
  LINE_DISCOUNT = 'lineDiscount',
  REELING_PRICE = 'reelingPrice',
  REELING_DISCOUNT = 'reelingDiscount',
  LINE_TARIFFS = 'lineTariffs',
  TOTAL_TARIFFS = 'totalTariffs',
  TOTAL_SHIPPING = 'totalShipping',
  TOTAL = 'total',
  TAX_TOTAL = 'taxTotal',
  SUBTOTAL = 'subtotal',
  PAYMENT_TERMS = 'paymentTerms',
  ORDER_NUMBER = 'orderNumber',
  CUSTOMER_PN = 'customerPn',
  QUANT_QUOTED = 'quantQuoted',
  QUANT_REQUESTED = 'quantRequested',
  DESCRIPTION = 'description',
  NOTES = 'notes',
  PACKAGING = 'packaging',
  PACKAGE_QUANTITY = 'packageQuantity',
  PRICE_BREAKS = 'priceBreaks',
  REVISION = 'revision',
  MANUFACTURER = 'manufacturer',
  MOQ = 'moq',
  NCNR = 'ncnr',
  NO_BID = 'noBid',
  MULTIPLE = 'multiple',
  LEAD_TIME = 'leadTime',
  EXPIRATION = 'expiration',
  EXTERNAL_ADJUSTMENT = 'externalAdjustment',
  LINE_NUMBER = 'lineNumber',
}

export const extractorFieldDataTypes = {
  [ExtractorFieldNames.MPN]: 'string',
  [ExtractorFieldNames.SKU]: 'string',
  [ExtractorFieldNames.CPID]: 'string',
  [ExtractorFieldNames.TRACKING_NUMBER]: 'string',
  [ExtractorFieldNames.COO]: 'string',
  [ExtractorFieldNames.HTSUS]: 'string',
  [ExtractorFieldNames.ECCN]: 'string',
  [ExtractorFieldNames.DATE_CODE]: 'string',
  [ExtractorFieldNames.MFG_LOT]: 'string',
  [ExtractorFieldNames.DOCUMENT_DATE]: 'date',
  [ExtractorFieldNames.SHIP_DATE]: 'date',
  [ExtractorFieldNames.DELIVERY_DATE]: 'date',
  [ExtractorFieldNames.RELEASE_DATE]: 'date',
  [ExtractorFieldNames.HAS_COFC]: 'boolean',
  [ExtractorFieldNames.QUANT_ORDERED]: 'number',
  [ExtractorFieldNames.QUANT_SHIPPED]: 'number',
  [ExtractorFieldNames.QUANT_BACKORDERED]: 'number',
  // eslint-disable-next-line no-restricted-syntax
  [ExtractorFieldNames.QUANT_CANCELLED]: 'number',
  [ExtractorFieldNames.UNIT_PRICE]: 'number',
  [ExtractorFieldNames.LINE_PRICE]: 'number',
  [ExtractorFieldNames.LINE_DISCOUNT]: 'number',
  [ExtractorFieldNames.REELING_PRICE]: 'number',
  [ExtractorFieldNames.REELING_DISCOUNT]: 'number',
  [ExtractorFieldNames.LINE_TARIFFS]: 'number',
  [ExtractorFieldNames.TOTAL_SHIPPING]: 'number',
  [ExtractorFieldNames.TOTAL]: 'number',
  [ExtractorFieldNames.TAX_TOTAL]: 'number',
  [ExtractorFieldNames.SUBTOTAL]: 'number',
  [ExtractorFieldNames.PAYMENT_TERMS]: 'string',
  [ExtractorFieldNames.CUSTOMER_PN]: 'string',
  [ExtractorFieldNames.QUANT_QUOTED]: 'number',
  [ExtractorFieldNames.QUANT_REQUESTED]: 'number',
  [ExtractorFieldNames.DESCRIPTION]: 'string',
  [ExtractorFieldNames.NOTES]: 'string',
  [ExtractorFieldNames.PACKAGING]: 'string',
  [ExtractorFieldNames.PACKAGE_QUANTITY]: 'number',
  [ExtractorFieldNames.PRICE_BREAKS]: 'string',
  [ExtractorFieldNames.REVISION]: 'string',
  [ExtractorFieldNames.MANUFACTURER]: 'string',
  [ExtractorFieldNames.MOQ]: 'number',
  [ExtractorFieldNames.NCNR]: 'boolean',
  [ExtractorFieldNames.NO_BID]: 'boolean',
  [ExtractorFieldNames.MULTIPLE]: 'boolean',
  [ExtractorFieldNames.LEAD_TIME]: 'number',
  [ExtractorFieldNames.EXPIRATION]: 'date',
  [ExtractorFieldNames.EXTERNAL_ADJUSTMENT]: 'number',
  [ExtractorFieldNames.LINE_NUMBER]: 'number',
};

export const documentLevelExtractorFieldNames = [
  ExtractorFieldNames.ORDER_NUMBER,
  ExtractorFieldNames.PAYMENT_TERMS,
  ExtractorFieldNames.SUBTOTAL,
  ExtractorFieldNames.TOTAL_SHIPPING,
  ExtractorFieldNames.TOTAL_TARIFFS,
  ExtractorFieldNames.TAX_TOTAL,
  ExtractorFieldNames.TOTAL,
  ExtractorFieldNames.TRACKING_NUMBER,
  ExtractorFieldNames.DOCUMENT_DATE,
];

export const lineLevelExtractorFieldNames = [
  ExtractorFieldNames.CPID,
  ExtractorFieldNames.MPN,
  ExtractorFieldNames.CUSTOMER_PN,
  ExtractorFieldNames.SKU,
  ExtractorFieldNames.QUANT_ORDERED,
  ExtractorFieldNames.QUANT_SHIPPED,
  ExtractorFieldNames.QUANT_BACKORDERED,
  // eslint-disable-next-line no-restricted-syntax
  ExtractorFieldNames.QUANT_CANCELLED,
  ExtractorFieldNames.QUANT_QUOTED,
  ExtractorFieldNames.QUANT_REQUESTED,
  ExtractorFieldNames.COO,
  ExtractorFieldNames.MFG_LOT,
  ExtractorFieldNames.DATE_CODE,
  ExtractorFieldNames.UNIT_PRICE,
  ExtractorFieldNames.LINE_PRICE,
  ExtractorFieldNames.LINE_TARIFFS,
  ExtractorFieldNames.LINE_DISCOUNT,
  ExtractorFieldNames.REELING_PRICE,
  ExtractorFieldNames.REELING_DISCOUNT,
  ExtractorFieldNames.SHIP_DATE,
  ExtractorFieldNames.RELEASE_DATE,
  ExtractorFieldNames.DELIVERY_DATE,
  ExtractorFieldNames.HTSUS,
  ExtractorFieldNames.ECCN,
  ExtractorFieldNames.HAS_COFC,
  ExtractorFieldNames.DESCRIPTION,
  ExtractorFieldNames.NOTES,
  ExtractorFieldNames.PACKAGING,
  ExtractorFieldNames.PACKAGE_QUANTITY,
  ExtractorFieldNames.PRICE_BREAKS,
  ExtractorFieldNames.REVISION,
  ExtractorFieldNames.MANUFACTURER,
  ExtractorFieldNames.MOQ,
  ExtractorFieldNames.NCNR,
  ExtractorFieldNames.NO_BID,
  ExtractorFieldNames.MULTIPLE,
  ExtractorFieldNames.LEAD_TIME,
  ExtractorFieldNames.EXPIRATION,
  ExtractorFieldNames.EXTERNAL_ADJUSTMENT,
  ExtractorFieldNames.LINE_NUMBER,
];

export type ExtractedDataFields = {
  [key in ExtractorFieldNames]?: string | number | boolean | null | DateString;
};

export type ExtractedData = ExtractedDataFields[];

export enum ValidationTypes {
  ERROR = 'error',
  WARNING = 'warning',
}

export type DocumentParsedErrorV2 = {
  key: ExtractorFieldNames;
  lineIndex?: number;
  message: string;
  name: string;
  severity: ValidationTypes;
  type: string;
  step?: StockDocumentProcessingStatus;
};

export type ExtractedDataRowV2 = ExtractedDataFields & {
  subLines?: ExtractedDataFields[];
};

export type ExtractedDataV2 = {
  lines?: ExtractedDataRowV2[];
} & ExtractedDataFields;

// structure informed by this section of flagship https://github.com/Cofactr/flagship/blob/master/inventory/core/document_extractor.py#L1375-L1438
export type ExtractorProperty = {
  type: string | number | boolean | null | DateString;
  description?: string;
  title?: string;
};

type LineProperties = {
  [key in ExtractorFieldNames]?: ExtractorProperty;
};

type ExtractorSubLine = {
  type: 'object';
  properties: LineProperties;
};

type ExtractorLine = {
  type: 'object';
  properties: LineProperties & {
    subLines?: {
      type: 'array';
      items: ExtractorSubLine;
    };
  };
};

export type ExtractorJsonSchema = {
  type: 'object';
  properties: {
    [key in ExtractorFieldNames]: ExtractorProperty;
  } & {
    lines?: {
      type: 'array';
      items: ExtractorLine;
    };
  };
};

export type DocumentParsedDataV2 = {
  errors?: DocumentParsedErrorV2[];
  extractedData?: ExtractedDataV2;
  jsonSchema?: ExtractorJsonSchema;
  plRequestIds?: string[] | null;
  text?: string | null;
  processUpdates?: {
    verb: 'create' | 'update';
    model: 'purchase_event' | 'purchase' | 'part_quote';
    data:
      | CreatePurchaseEventRequest
      | CreateUpdatePurchaseOrder
      | PartQuoteCreateUpdate;
  }[];
};

export type DocumentParsedData = {
  errors?: {
    type: string;
    message: string;
    step?: StockDocumentProcessingStatus;
    severity?: ValidationTypes;
    lineIndex?: number;
  }[];
  extractedData?: ExtractedData | null;
  foundValues?: string[] | null;
  text?: string | null;
  plRequestIds?: string[] | null;
  processUpdates?: {
    verb: 'create' | 'update';
    model: 'purchase_event' | 'purchase' | 'part_quote';
    data:
      | CreatePurchaseEventRequest
      | CreateUpdatePurchaseOrder
      | PartQuoteCreateUpdate;
  }[];
};

export type DocumentReview = {
  needsReview?: boolean;
  processUpdates?: boolean;
  scoreExtraction?: number;
  parsedData?: DocumentParsedData | DocumentParsedDataV2; // TODO: Handle V1 structure will be deprecated in the near future
  purchase?: string | null;
  poNumber?: string | null;
  documentExtractor?: string | null;
  reprocess?: boolean;
  processingStatus?: StockDocumentProcessingStatus;
};

export enum ExtractorContextTypes {
  EMAIL = 'email',
  SCAN = 'scan',
  UPLOAD = 'upload',
  ANY = 'any',
  WEBSITE = 'website',
}

export enum OcrType {
  TEXTRACT = 'textract',
  MARKER = 'marker',
  TABULA = 'tabula',
  EMBEDDED = 'embedded',
  NONE = 'none',
}

export enum ExtractorModel {
  OPUS = 'claude-3-opus-20240229',
  SONNET = 'claude-3-sonnet-20240229',
  SONNET_35 = 'claude-3-5-sonnet-20240620',
  HAIKU = 'claude-3-haiku-20240307',
  GEMINI_15_FLASH = 'gemini-1.5-flash-001',
  GEMINI_15_PRO = 'gemini-1.5-pro-001',
}

export enum ExtractorSchemaType {
  PURCHASE_V1 = 'purchase_v1',
  PURCHASE_V2 = 'purchase_v2',
  QUOTE_V1 = 'quote_v1',
}

export type DocumentExtractor = {
  id: string;
  name: string;
  active: boolean;
  internalNotes: string | null;
  externalNotes: string | null;
  matchToSource: ExtractorContextTypes;
  default: boolean;
  supplierObjectIds: string[];
  documentType: StockDocType;
  matchSubjectRegex: string | null;
  matchBodyRegex: string | null;
  matchDocumentRegex: string | null;
  matchSenderRegex: string | null;
  matchRecipientRegex: string | null;
  matchPriority: number;
  ocrType: OcrType;
  excludePageRegex: string | null;
  ignoreAttachments: boolean;
  extractorModel: ExtractorModel;
  schemaType: ExtractorSchemaType;
  purchaseEventTypes: OrderUpdateStatus[];
  org: string | null;
};

export type StockDocument = {
  id: string;
  name: string;
  documentType: StockDocType;
  notes: string | null;
  document: string | null;
  createdAt: DateTimeString | null;
  relatedRecords: RelatedRecord[];
  needsReview: boolean;
  documentExtractor: string | null;
  documentExtractorId: string | null;
  purchase: string | null;
  org: string | OrgLite | null;
  processingStartedAt: DateTimeString | null;
  metadata: {
    context: {
      type: ExtractorContextTypes;
      senderEmail?: string | null;
      recipientEmails?: string | null;
      subject?: string | null;
      bodyText?: string | null;
      bodyHtml?: string | null;
      barcodes?: { [key: string]: string };
    };
  };
  parsedData: DocumentParsedData | DocumentParsedDataV2; // NOTE: handling v1 is temporary until we have a v2 kinks worked out
  supplier?: SupplierLite | null;
  orgSupplier?: OrgSupplier | null;
  mimeType: string | null;
  processingStatus?: StockDocumentProcessingStatus;
};

export type StockLocationCreate = {
  orgId?: string | null;
  parentId?: string | null;
  name: string;
  locationType?: StockLocationType;
  shipmentId?: string | null;
  kitRequestId?: string | null;
  billable?: boolean;
  externalName?: string | null;
  notificationEmail?: string;
  consumesStock?: boolean;
  manufacturer?: boolean;
  requiresLcrInspection?: boolean;
  requiresDimensionalInspection?: boolean;
  requiresReels?: boolean;
  requiresMerges?: boolean;
  address?: OrgAddressBase;
  acceptsSmtNoPnp?: boolean;
  team?: Team['id'];
  customProperties?: object;
};

export type StockLocationUpdate = {
  name?: string;
  externalName?: string | null;
  billable?: boolean;
  locationType?: StockLocationType;
  address?: OrgAddressBase;
  orgId?: Org['id'];
  parentId?: string | null;
  shipmentId?: Shipment['id'] | null;
  kitRequestId?: string | null;
  locked?: boolean;
  notificationEmail?: string;
  manufacturer?: boolean;
  requiresMslReset?: boolean;
  requiresLcrInspection?: boolean;
  requiresDimensionalInspection?: boolean;
  acceptsTube?: boolean;
  acceptsSmtNoPnp?: boolean;
  requiresReels?: boolean;
  requiresMerges?: boolean;
  requiresSplits?: boolean;
  consumesStock?: boolean;
  longTermStorage?: boolean;
  team?: Team['id'];
  customProperties?: object;
};

export type StockLotCreate = {
  id?: string;
  part?: string;
  stockLocationId?: string | null;
  facilityId?: string | null;
  package?: string | null;
  quant?: number;
  dateCode?: string | null;
  mfgLot?: string | null;
  msl?: string;
  mslFloorHours?: number;
  pending?: boolean;
  quantCertain?: boolean;
  quarantined?: boolean;
  expectedBy?: DateString | string | null;
  internalNotes?: string | null;
  externalNotes?: string | null;
  tapeSize?: number | null;
  createdByCustomer?: boolean;
  station?: string;
  unitPricePaid?: number | null;
  landedUnitPricePaid?: number | null;
  smtNoPnp?: boolean;
  expiresAt?: DateString | null;
  team?: TeamLite['id'] | null;
} & Pick<V2OrgPartTypes, 'v2OrgPart'>;

export type StockLotReceive = {
  stockLocation: StockLocation['id'] | null;
  package?: PackagingOptions | null;
  quant: number;
  dateCode?: string | null;
  mfgLot?: string | null;
  msl?: string | null;
  mslFloorHours?: number;
  quantCertain: boolean;
  quarantined?: boolean;
  internalNotes?: string | null;
  externalNotes?: string | null;
  printedNotes?: string | null;
  tapeSize?: number;
  station?: StockLocation['name'];
  shipmentLine?: ShipmentLine['id'] | null;
  smtNoPnp?: boolean;
  expiresAt?: DateString | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  weight?: number | null;
};

export type AllocationCRUDSchema = {
  id: string;
  name: string | null;
  originalQuant: number;
  quant: number;
  releasedAt: string | null;
  part: Part['id'];
  org: Org;
  releasedBy: User;
  stockLot: StockLotCRUDSchema;
  relatedRecords: RelatedRecord[];
};

/**
 * Schema Types will be the full possible response from the endpoint with all nested fields expanded
 * This is used for the useServerSideQuery hook
 */
export type AllocationQuerySchema = {
  id: string;
  /** --------------------------------- */
  /** --- Deprecated Part V1 Fields --- */
  /** --------------------------------- */
  part: Part;
  orgPart: OrgPart;
  syncedPart: Part | null;
  /** -------------------------- */
  /** --- New Part V2 Fields --- */
  /** -------------------------- */
  partVersion: PART_VERSION;
  v2OrgPart: OrgPartSchema;
  /** ------------------------ */
  /** --- Remaining Fields --- */
  /** ------------------------ */
  org: Org;
  releasedBy: User;
  stockLot: StockLotQuerySchemaLite;
  productionRun: ProductionRunLite;
  kitRequestLine: KitRequestLineSchemaLite;
  kitRequest: KitRequestSchemaLite;
  status: string;
  createdDate: string;
  updatedAt: string;
  originalQuant: number;
  quant: number;
  name: string | null;
  releasedAt: string | null;
  releaseNotes: string | null;
  dataSource: string | null;
  splitFrom: string | null;
};

export type AllocationQuerySchemaLite = Lite<AllocationQuerySchema>;

export type AllocationsCreate = {
  stockLot: StockLotCRUDSchema['id'];
  quant: number;
  productionRun: ProductionRun['id'];
  replaceFromProductionRun?: ProductionRun['id'];
};

export type DataSource = 'google-sheets';

export type StockLotScanMove = {
  stockLocationId: string;
};

export enum PackagingOptions {
  TUBE = 'tube',
  TRAY = 'tray',
  BULK = 'bulk',
  TAPE_SINGLE = 'tape_single',
  TAPE_MULTIPLE = 'tape_multiple',
  REEL = 'reel',
  UNKNOWN = 'unknown',
}

export const PackagingOptionsMap: Record<PackagingOptions, string> = {
  [PackagingOptions.TUBE]: 'Tube',
  [PackagingOptions.TRAY]: 'Tray',
  [PackagingOptions.BULK]: 'Bulk',
  [PackagingOptions.TAPE_SINGLE]: 'Cut Tape (Single)',
  [PackagingOptions.TAPE_MULTIPLE]: 'Cut Tape (Multiple)',
  [PackagingOptions.REEL]: 'Reel',
  [PackagingOptions.UNKNOWN]: 'Unknown',
};

export enum MslOptions {
  msl1 = '1',
  msl2 = '2',
  msl2a = '2a',
  msl3 = '3',
  msl4 = '4',
  msl5 = '5',
  msl5a = '5a',
  msl6 = '6',
}

export const mslAllowedExposureHours = {
  '2': 8760,
  '2a': 672,
  '3': 168,
  '4': 72,
  '5': 48,
  '5a': 24,
  '6': 0,
};

export type HandlingTaskRow = {
  id: string;
  status: string;
  description: string;
  notes: string;
  requestType: HandlingRequestType;
  submittedAt: string;
  completedAt: string;
  org?: string;
  stockLot?: StockLotLite;
  location: StockLocationLite;
  relatedRecords: RelatedRecord[];
  voidable: boolean;
  taskType: string;
  taskTypeDisplay: string;
  shipmentCode?: string | null;
};

export enum RelatedRecordType {
  BOM = 'bom',
  PURCHASE_ORDER = 'purchase_order',
  PRODUCTION_RUN = 'production_run',
  STOCK_LOT = 'stock_lot',
  STOCK_LOCATION = 'stock_location',
  SHIPMENT = 'shipment',
  SNAPSHOT = 'snapshot',
  ORDER = 'order',
  USER = 'user',
  TEAM = 'team',
  KIT_REQUEST = 'kit_request',
  SUPPLIER_BILL = 'supplier_bill',
  PURCHASE_REQUEST_GROUP = 'purchase_request_group',
  RFQ = 'rfq',
  SUPPLIER_QUOTE = 'supplier_quote',
  PART = 'part',
  CART_PURCHASE_REQUEST_GROUP = 'cart_purchase_request_group',
  RFQ_PURCHASE_REQUEST_GROUP = 'rfq_purchase_request_group',
  SUPPLIER_PURCHASE_ORDER = 'supplier_purchase_order',
}

export type RelatedRecordMenuItem = {
  Icon?: Icon;
  label: string;
  onClick: () => void;
  iconColor?: ColorOptions;
  disabled?: boolean;
};

export type RelatedRecord = {
  name: string;
  recordType: RelatedRecordType;
  recordId: string;
  orgId: string | null;
  Icon?: Icon;
  additionalMenuItems?: RelatedRecordMenuItem[];
};

export type HandlingRequestCreate = {
  orgId: string;
  requestType: HandlingRequestType;
  stockLotIds: string[];
  notes?: string;
  deadline?: string | Date;
  quant?: number;
  stockLocationId?: string;
  package?: string;
};

export enum HandlingRequestStatus {
  DRAFT = 'draft',
  REQUESTED = 'requested',
  // eslint-disable-next-line no-restricted-syntax
  CANCELLED = 'cancelled',
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  PASSED = 'passed',
  FAILED = 'failed',
}

export type HandlingRequestLite = {
  id: string;
  requestType: HandlingRequestType;
  requestTypeDisplay: string;
  quant?: number;
  notes?: string;
  deadline?: DateString | Date;
  package?: PackagingOptions;
  status: HandlingRequestStatus;
  completedAt: DateTimeString | null;
  // eslint-disable-next-line no-restricted-syntax
  cancelledAt: DateTimeString | null;
  pickedAt: DateTimeString | null;
};

export type HandlingRequest = HandlingRequestLite & {
  billable: boolean;
  // eslint-disable-next-line no-restricted-syntax
  cancelled: boolean;
  completedAt: string | null;
  org: Org['id'];
  status: HandlingRequestStatus;
  stockLocation: StockLocation['id'];
  stockLot: StockLotCRUDSchema['id'];
  splitStockLot: StockLotCRUDSchema['id'] | null;
  submittedAt: DateTimeString;
  pickedAt: DateTimeString | null;
};

export type ExpandedHandlingRequest = Omit<
  HandlingRequest,
  'stockLot' | 'splitStockLot'
> & {
  stockLot: StockLotCRUDSchema;
  splitStockLot: StockLotCRUDSchema | null;
};

export type DeadlineRequest = {
  deadlineRequests: DeadlineRequestItem[];
};

export type DeadlineRequestItem = {
  stockLotId: StockLotCRUDSchema['id'];
  requestTypes: HandlingRequestType[];
};

export type Deadline = {
  stockLotId: StockLotCRUDSchema['id'];
  requestTypes: HandlingRequestType[];
  fromDate: DateString;
  deadline: DateString;
  daysAfter: number;
};

// [ msl_reset, splice, combine, reel, anticounterfeiting, split, move, check_quant, dispose, repackage ]

export enum HandlingRequestType {
  MSL_RESET = 'msl_reset',
  SPLICE = 'splice',
  COMBINE = 'combine',
  REEL = 'reel',
  ANTICOUNTERFEITING = 'anticounterfeiting',
  SPLIT = 'split',
  TRANSFER = 'transfer',
  CONSUME = 'consume',
  CHECK_QUANT = 'check_quant',
  DISPOSE = 'dispose',
  REPACKAGE = 'repackage',
  RECEIVE = 'receive',
  INSPECTION_PHOTO = 'inspection_photo',
  INSPECTION_PHOTO_SAMPLE = 'inspection_photo_sample',
  INSPECTION_PHOTO_FULL = 'inspection_photo_full',
  XRAY = 'x_ray',
  X_RAY_SAMPLE = 'x_ray_sample',
  X_RAY_FULL = 'x_ray_full',
  PACKAGE_PHOTO = 'package_photo',
  INSPECTION_DIMENSIONAL = 'inspection_dimensional',
  INSPECTION_LCR = 'inspection_lcr',
  ANTICOUNTERFEITING_SAMPLE = 'anticounterfeiting_sample',
  ANTICOUNTERFEITING_FULL = 'anticounterfeiting_full',
  INSPECTION_SURFACE = 'inspection_surface',
  INSPECTION_DECAP = 'inspection_decap',
  INSPECTION_3RD_PARTY = 'inspection_3rd_party',
  SHIP = 'ship',
  RESELL = 'resell',
  KIT = 'kit',
}

export const executionOrder = [
  HandlingRequestType.PACKAGE_PHOTO,
  HandlingRequestType.DISPOSE,
  HandlingRequestType.CHECK_QUANT,
  HandlingRequestType.INSPECTION_LCR,
  HandlingRequestType.INSPECTION_DIMENSIONAL,
  HandlingRequestType.INSPECTION_PHOTO,
  HandlingRequestType.INSPECTION_PHOTO_SAMPLE,
  HandlingRequestType.INSPECTION_PHOTO_FULL,
  HandlingRequestType.XRAY,
  HandlingRequestType.X_RAY_SAMPLE,
  HandlingRequestType.X_RAY_FULL,
  HandlingRequestType.INSPECTION_SURFACE,
  HandlingRequestType.INSPECTION_DECAP,
  HandlingRequestType.INSPECTION_3RD_PARTY,
  HandlingRequestType.ANTICOUNTERFEITING_SAMPLE,
  HandlingRequestType.ANTICOUNTERFEITING_FULL,
  HandlingRequestType.MSL_RESET,
  HandlingRequestType.SPLIT,
  HandlingRequestType.SPLICE,
  HandlingRequestType.COMBINE,
  HandlingRequestType.REEL,
  HandlingRequestType.REPACKAGE,
  HandlingRequestType.SHIP,
  HandlingRequestType.RESELL,
];

export const HandlingRequestTypeLabels: {
  [key in HandlingRequestType]: string;
} = {
  [HandlingRequestType.MSL_RESET]: 'MSL Reset',
  [HandlingRequestType.SPLICE]: 'Splice',
  [HandlingRequestType.COMBINE]: 'Combine',
  [HandlingRequestType.REEL]: 'Reel',
  [HandlingRequestType.ANTICOUNTERFEITING]: 'Anti-counterfeiting',
  [HandlingRequestType.SPLIT]: 'Split',
  [HandlingRequestType.TRANSFER]: 'Transfer',
  [HandlingRequestType.CHECK_QUANT]: 'Check Quantity',
  [HandlingRequestType.DISPOSE]: 'Dispose',
  [HandlingRequestType.REPACKAGE]: 'Repackage',
  [HandlingRequestType.RECEIVE]: 'Receive',
  [HandlingRequestType.CONSUME]: 'Consume',
  [HandlingRequestType.INSPECTION_PHOTO]: 'Inspection Photo',
  [HandlingRequestType.INSPECTION_PHOTO_SAMPLE]: 'Inspection Photo Sample',
  [HandlingRequestType.INSPECTION_PHOTO_FULL]: 'Inspection Photo Full',
  [HandlingRequestType.XRAY]: 'X-Ray',
  [HandlingRequestType.X_RAY_SAMPLE]: 'X-Ray Sample',
  [HandlingRequestType.X_RAY_FULL]: 'X-Ray Full',
  [HandlingRequestType.PACKAGE_PHOTO]: 'Package Photo',
  [HandlingRequestType.INSPECTION_DIMENSIONAL]: 'Dimensional Inspection',
  [HandlingRequestType.INSPECTION_LCR]: 'LCR Inspection',
  [HandlingRequestType.ANTICOUNTERFEITING_SAMPLE]: 'Anti-counterfeiting Sample',
  [HandlingRequestType.ANTICOUNTERFEITING_FULL]: 'Anti-counterfeiting Full',
  [HandlingRequestType.INSPECTION_SURFACE]: 'Surface Inspection',
  [HandlingRequestType.INSPECTION_DECAP]: 'Decap Inspection',
  [HandlingRequestType.INSPECTION_3RD_PARTY]: 'Third-party Inspection',
  [HandlingRequestType.SHIP]: 'Ship',
  [HandlingRequestType.RESELL]: 'Resell',
  [HandlingRequestType.KIT]: 'Kit',
};

export enum StockEventType {
  RECEIVE = 'receive',
  MOVE = 'move',
  ADJUST_QUANT = 'adjust_quant',
  ADJUST_OWNERSHIP = 'adjust_ownership',
  SPLIT = 'split',
  DISPOSE = 'dispose',
  KIT = 'kit',
  SHIP = 'ship',
  CONSUME = 'consume',
  REPACKAGE = 'repackage',
  NOTE = 'note',
  CHANGE_PART = 'change_part',
  PACKAGE_PHOTO = 'package_photo',
  SOLDERABILITY = 'solderability',
  MSL_TRACK = 'msl_track',
  USER_CREATE = 'user_create',
  SPLICE = 'splice',
  COMBINE = 'combine',
  PURCHASE = 'purchase',
  INSPECTION_PHOTO = 'inspection_photo',
  INSPECTION_PHOTO_SAMPLE = 'inspection_photo_sample',
  INSPECTION_PHOTO_FULL = 'inspection_photo_full',
  X_RAY = 'x_ray',
  X_RAY_SAMPLE = 'x_ray_sample',
  X_RAY_FULL = 'x_ray_full',
  ANTI_COUNTERFEITING = 'anticounterfeiting',
  INSPECTION_SURFACE = 'inspection_surface',
  INSPECTION_DECAP = 'inspection_decap',
  INSPECTION_3RD_PARTY = 'inspection_3rd_party',
  INSPECTION_DIMENSIONAL = 'inspection_dimensional',
  INSPECTION_LCR = 'inspection_lcr',
  RESELL = 'resell',
}

export const StockEventTypeLabels: {
  [key in StockEventType]: string;
} = {
  [StockEventType.RECEIVE]: 'Receive',
  [StockEventType.MOVE]: 'Move',
  [StockEventType.ADJUST_QUANT]: 'Adjust Quantity',
  [StockEventType.ADJUST_OWNERSHIP]: 'Adjust Ownership',
  [StockEventType.SPLIT]: 'Split',
  [StockEventType.DISPOSE]: 'Dispose',
  [StockEventType.KIT]: 'Kit',
  [StockEventType.SHIP]: 'Ship',
  [StockEventType.CONSUME]: 'Consume',
  [StockEventType.REPACKAGE]: 'Repackage',
  [StockEventType.NOTE]: 'Note',
  [StockEventType.CHANGE_PART]: 'Change Part',
  [StockEventType.PACKAGE_PHOTO]: 'Package Photo',
  [StockEventType.SOLDERABILITY]: 'Solderability',
  [StockEventType.MSL_TRACK]: 'MSL Tracking',
  [StockEventType.USER_CREATE]: 'User Create',
  [StockEventType.SPLICE]: 'Splice',
  [StockEventType.COMBINE]: 'Combine',
  [StockEventType.PURCHASE]: 'Purchase',
  [StockEventType.INSPECTION_PHOTO]: 'Inspection Photo',
  [StockEventType.INSPECTION_PHOTO_SAMPLE]: 'Inspection Photo Sample',
  [StockEventType.INSPECTION_PHOTO_FULL]: 'Inspection Photo Full',
  [StockEventType.X_RAY]: 'X-Ray',
  [StockEventType.X_RAY_SAMPLE]: 'X-Ray Sample',
  [StockEventType.X_RAY_FULL]: 'X-Ray Full',
  [StockEventType.ANTI_COUNTERFEITING]: 'Anti-counterfeiting',
  [StockEventType.INSPECTION_SURFACE]: 'Surface Inspection',
  [StockEventType.INSPECTION_DECAP]: 'Decap Inspection',
  [StockEventType.INSPECTION_3RD_PARTY]: 'Third-party Inspection',
  [StockEventType.INSPECTION_DIMENSIONAL]: 'Dimensional Inspection',
  [StockEventType.INSPECTION_LCR]: 'LCR Inspection',
  [StockEventType.RESELL]: 'Resell',
};

export const StockEventTypeIcon: { [key in StockEventType]: Icon } = {
  [StockEventType.RECEIVE]: IconTruckDelivery,
  [StockEventType.MOVE]: IconTransferIn,
  [StockEventType.ADJUST_QUANT]: IconTallymarks,
  [StockEventType.ADJUST_OWNERSHIP]: IconBuildingStore,
  [StockEventType.SPLIT]: IconCut,
  [StockEventType.DISPOSE]: IconTrash,
  [StockEventType.KIT]: IconBox,
  [StockEventType.SHIP]: IconPlaneDeparture,
  [StockEventType.CONSUME]: IconCalendarEvent,
  [StockEventType.REPACKAGE]: IconDisc,
  [StockEventType.NOTE]: IconNote,
  [StockEventType.CHANGE_PART]: IconCpu,
  [StockEventType.PACKAGE_PHOTO]: IconCamera,
  [StockEventType.SOLDERABILITY]: IconMicroscope,
  [StockEventType.MSL_TRACK]: IconDropletOff,
  [StockEventType.USER_CREATE]: IconPlus,
  [StockEventType.SPLICE]: IconArrowsJoin,
  [StockEventType.COMBINE]: IconArrowsJoin,
  [StockEventType.PURCHASE]: IconFileDollar,
  [StockEventType.INSPECTION_PHOTO]: IconMicroscope,
  [StockEventType.INSPECTION_PHOTO_SAMPLE]: IconMicroscope,
  [StockEventType.INSPECTION_PHOTO_FULL]: IconMicroscope,
  [StockEventType.X_RAY]: IconRadioactive,
  [StockEventType.X_RAY_SAMPLE]: IconRadioactive,
  [StockEventType.X_RAY_FULL]: IconRadioactive,
  [StockEventType.ANTI_COUNTERFEITING]: IconMicroscope,
  [StockEventType.INSPECTION_SURFACE]: IconMicroscope,
  [StockEventType.INSPECTION_DECAP]: IconMicroscope,
  [StockEventType.INSPECTION_3RD_PARTY]: IconMicroscope,
  [StockEventType.INSPECTION_DIMENSIONAL]: IconRulerMeasure,
  [StockEventType.INSPECTION_LCR]: IconCircuitResistor,
  [StockEventType.RESELL]: IconBuildingStore,
};

export const HandlingRequestTypeIcon: {
  [key in HandlingRequestType]: Icon;
} = {
  [HandlingRequestType.MSL_RESET]: IconDropletOff,
  [HandlingRequestType.SPLICE]: IconArrowsJoin,
  [HandlingRequestType.COMBINE]: IconArrowsJoin,
  [HandlingRequestType.REEL]: IconDisc,
  [HandlingRequestType.ANTICOUNTERFEITING]: IconMicroscope,
  [HandlingRequestType.ANTICOUNTERFEITING_SAMPLE]: IconMicroscope,
  [HandlingRequestType.ANTICOUNTERFEITING_FULL]: IconMicroscope,
  [HandlingRequestType.SPLIT]: IconCut,
  [HandlingRequestType.KIT]: IconBox,
  [HandlingRequestType.SHIP]: IconPlaneDeparture,
  [HandlingRequestType.CONSUME]: IconCalendarEvent,
  [HandlingRequestType.REPACKAGE]: IconDisc,
  [HandlingRequestType.CHECK_QUANT]: IconTallymarks,
  [HandlingRequestType.TRANSFER]: IconCpu,
  [HandlingRequestType.PACKAGE_PHOTO]: IconCamera,
  [HandlingRequestType.INSPECTION_PHOTO]: IconMicroscope,
  [HandlingRequestType.INSPECTION_PHOTO_SAMPLE]: IconMicroscope,
  [HandlingRequestType.INSPECTION_PHOTO_FULL]: IconMicroscope,
  [HandlingRequestType.XRAY]: IconRadioactive,
  [HandlingRequestType.X_RAY_SAMPLE]: IconRadioactive,
  [HandlingRequestType.X_RAY_FULL]: IconRadioactive,
  [HandlingRequestType.INSPECTION_SURFACE]: IconMicroscope,
  [HandlingRequestType.INSPECTION_DECAP]: IconMicroscope,
  [HandlingRequestType.INSPECTION_3RD_PARTY]: IconMicroscope,
  [HandlingRequestType.INSPECTION_DIMENSIONAL]: IconRulerMeasure,
  [HandlingRequestType.INSPECTION_LCR]: IconCircuitResistor,
  [HandlingRequestType.RESELL]: IconBuildingStore,
  [HandlingRequestType.DISPOSE]: IconTrash,
  [HandlingRequestType.RECEIVE]: IconTruckDelivery,
};

export enum StockDocType {
  INSPECTION_PHOTO = 'inspection_photo',
  INSPECTION_PHOTO_BULK = 'inspection_photo_bulk',
  PACKAGE_PHOTO = 'package_photo',
  X_RAY = 'x_ray',
  X_RAY_BULK = 'x_ray_bulk',
  DOCUMENT = 'document',
  REPORT = 'report',
  QUOTE = 'quote',
  PACKING_SLIP = 'packing_slip',
  COFC = 'c_of_c',
  PURCHASE_UPDATE = 'purchase_update',
  PURCHASE_CONFIRMATION = 'purchase_confirmation',
  INVOICE = 'invoice',
  SHIP_NOTIFICATION = 'ship_notification',
  PURCHASE_ORDER = 'purchase_order',
  RFQ = 'rfq',
  FILE = 'file',
  BILL_PAYMENT = 'bill_payment',
  PURCHASE_REQUEST = 'purchase_request',
  PCN = 'pcn',
  DATASHEET = 'datasheet',
  MSDS = 'msds',
  COA = 'coa',
  CERTIFICATION = 'certification',
  ROHS_COMPLIANCE = 'rohs_compliance',
  REACH_COMPLIANCE = 'reach_compliance',
  CALIBRATION_CERTIFICATE = 'calibration_certificate',
  QUALITY_INSPECTION_REPORT = 'quality_inspection_report',
  NONCONFORMANCE_REPORT = 'nonconformance_report',
  CORRECTIVE_ACTION_REPORT = 'corrective_action_report',
  ASSEMBLY_INSTRUCTIONS = 'assembly_instructions',
  BOL = 'bol',
  CMR = 'cmr',
  CUSTOMS_DECLARATION = 'customs_declaration',
  EXPORT_LICENSE = 'export_license',
  ORIGIN_CERTIFICATE = 'origin_certificate',
  INSURANCE_CERTIFICATE = 'insurance_certificate',
  TECHNICAL_DRAWING = 'technical_drawing',
  ECO = 'eco',
  TEST_REPORT = 'test_report',
  FAILURE_ANALYSIS = 'failure_analysis',
  DIMENSIONAL_REPORT = 'dimensional_report',
  FIRST_ARTICLE_REPORT = 'first_article_report',
  SUPPLIER_AUDIT = 'supplier_audit',
  CAPABILITY_STATEMENT = 'capability_statement',
  CONTRACT = 'contract',
  CAD_FILE = 'cad_file',
  CENTROIDS = 'centroids',
  ORDER_STATUS_REPORT = 'order_status_report',
  BILL_OF_MATERIALS = 'bill_of_materials',
  DOCUMENT_PACKAGE = 'document_package',
}

export const StockDocTypeLabels: { [key in StockDocType]: string } = {
  [StockDocType.INSPECTION_PHOTO]: 'Inspection Photo',
  [StockDocType.INSPECTION_PHOTO_BULK]: 'Inspection Photo (Bulk)',
  [StockDocType.PACKAGE_PHOTO]: 'Package Photo',
  [StockDocType.X_RAY]: 'X-Ray Image',
  [StockDocType.X_RAY_BULK]: 'X-Ray Image (Bulk)',
  [StockDocType.DOCUMENT]: 'Document',
  [StockDocType.REPORT]: 'Report',
  [StockDocType.QUOTE]: 'Quote',
  [StockDocType.PACKING_SLIP]: 'Packing Slip',
  [StockDocType.COFC]: 'Certificate of Conformance',
  [StockDocType.PURCHASE_UPDATE]: 'Purchase Update',
  [StockDocType.PURCHASE_CONFIRMATION]: 'Purchase Confirmation',
  [StockDocType.INVOICE]: 'Invoice',
  [StockDocType.SHIP_NOTIFICATION]: 'Shipment Notification',
  [StockDocType.PURCHASE_ORDER]: 'Purchase Order',
  [StockDocType.RFQ]: 'RFQ',
  [StockDocType.FILE]: 'File',
  [StockDocType.BILL_PAYMENT]: 'Bill Payment',
  [StockDocType.PURCHASE_REQUEST]: 'Requisition',
  [StockDocType.PCN]: 'Product Change Notification',
  [StockDocType.DATASHEET]: 'Datasheet',
  [StockDocType.MSDS]: 'Material Safety Data Sheet',
  [StockDocType.COA]: 'Certificate of Analysis',
  [StockDocType.CERTIFICATION]: 'Certification',
  [StockDocType.ROHS_COMPLIANCE]: 'RoHS Compliance',
  [StockDocType.REACH_COMPLIANCE]: 'REACH Compliance',
  [StockDocType.CALIBRATION_CERTIFICATE]: 'Calibration Certificate',
  [StockDocType.QUALITY_INSPECTION_REPORT]: 'Quality Inspection Report',
  [StockDocType.NONCONFORMANCE_REPORT]: 'Nonconformance Report',
  [StockDocType.CORRECTIVE_ACTION_REPORT]: 'Corrective Action Report',
  [StockDocType.ASSEMBLY_INSTRUCTIONS]: 'Assembly Instructions',
  [StockDocType.BOL]: 'Bill of Lading',
  [StockDocType.CMR]: 'Commercial Invoice',
  [StockDocType.CUSTOMS_DECLARATION]: 'Customs Declaration',
  [StockDocType.EXPORT_LICENSE]: 'Export License',
  [StockDocType.ORIGIN_CERTIFICATE]: 'Origin Certificate',
  [StockDocType.INSURANCE_CERTIFICATE]: 'Insurance Certificate',
  [StockDocType.TECHNICAL_DRAWING]: 'Technical Drawing',
  [StockDocType.ECO]: 'ECO Declaration',
  [StockDocType.TEST_REPORT]: 'Test Report',
  [StockDocType.FAILURE_ANALYSIS]: 'Failure Analysis',
  [StockDocType.DIMENSIONAL_REPORT]: 'Dimensional Report',
  [StockDocType.FIRST_ARTICLE_REPORT]: 'First Article Report',
  [StockDocType.SUPPLIER_AUDIT]: 'Supplier Audit',
  [StockDocType.CAPABILITY_STATEMENT]: 'Capability Statement',
  [StockDocType.CONTRACT]: 'Contract',
  [StockDocType.CAD_FILE]: 'CAD File',
  [StockDocType.CENTROIDS]: 'Centroids',
  [StockDocType.ORDER_STATUS_REPORT]: 'Order Status Report',
  [StockDocType.BILL_OF_MATERIALS]: 'Bill of Materials',
  [StockDocType.DOCUMENT_PACKAGE]: 'Document Package',
};

export const StockDocTypeIcons: { [key in StockDocType]: Icon } = {
  [StockDocType.INSPECTION_PHOTO]: IconFileTypeJpg,
  [StockDocType.INSPECTION_PHOTO_BULK]: IconFileZip,
  [StockDocType.PACKAGE_PHOTO]: IconFileTypeJpg,
  [StockDocType.X_RAY]: IconFileTypeJpg,
  [StockDocType.X_RAY_BULK]: IconFileZip,
  [StockDocType.DOCUMENT]: IconFile,
  [StockDocType.REPORT]: IconFileAnalytics,
  [StockDocType.QUOTE]: IconFileDollar,
  [StockDocType.PACKING_SLIP]: IconFileBarcode,
  [StockDocType.COFC]: IconFileCertificate,
  [StockDocType.PURCHASE_UPDATE]: IconFileDollar,
  [StockDocType.PURCHASE_CONFIRMATION]: IconFileDollar,
  [StockDocType.INVOICE]: IconFileDollar,
  [StockDocType.SHIP_NOTIFICATION]: IconFileBarcode,
  [StockDocType.PURCHASE_ORDER]: IconFileDollar,
  [StockDocType.RFQ]: IconFileDollar,
  [StockDocType.FILE]: IconFile,
  [StockDocType.BILL_PAYMENT]: IconFileDollar,
  [StockDocType.PURCHASE_REQUEST]: IconFileDollar,
  [StockDocType.PCN]: IconFileDelta,
  [StockDocType.DATASHEET]: IconFileInfo,
  [StockDocType.MSDS]: IconFileAlert,
  [StockDocType.COA]: IconFileCertificate,
  [StockDocType.CERTIFICATION]: IconFileCertificate,
  [StockDocType.ROHS_COMPLIANCE]: IconFileCertificate,
  [StockDocType.REACH_COMPLIANCE]: IconFileCertificate,
  [StockDocType.CALIBRATION_CERTIFICATE]: IconFileCertificate,
  [StockDocType.QUALITY_INSPECTION_REPORT]: IconFileAnalytics,
  [StockDocType.NONCONFORMANCE_REPORT]: IconFileAnalytics,
  [StockDocType.CORRECTIVE_ACTION_REPORT]: IconFileAnalytics,
  [StockDocType.ASSEMBLY_INSTRUCTIONS]: IconFilePencil,
  [StockDocType.BOL]: IconFileBarcode,
  [StockDocType.CMR]: IconFileDollar,
  [StockDocType.CUSTOMS_DECLARATION]: IconFileDollar,
  [StockDocType.EXPORT_LICENSE]: IconFileCertificate,
  [StockDocType.ORIGIN_CERTIFICATE]: IconFileCertificate,
  [StockDocType.INSURANCE_CERTIFICATE]: IconFileCertificate,
  [StockDocType.TECHNICAL_DRAWING]: IconFilePencil,
  [StockDocType.ECO]: IconFilePencil,
  [StockDocType.TEST_REPORT]: IconFileCheck,
  [StockDocType.FAILURE_ANALYSIS]: IconFileAnalytics,
  [StockDocType.DIMENSIONAL_REPORT]: IconFileAnalytics,
  [StockDocType.FIRST_ARTICLE_REPORT]: IconFileCheck,
  [StockDocType.SUPPLIER_AUDIT]: IconFileCheck,
  [StockDocType.CAPABILITY_STATEMENT]: IconFileAnalytics,
  [StockDocType.CONTRACT]: IconFileAnalytics,
  [StockDocType.CAD_FILE]: IconFile3d,
  [StockDocType.CENTROIDS]: IconFileSpreadsheet,
  [StockDocType.ORDER_STATUS_REPORT]: IconFileSpreadsheet,
  [StockDocType.BILL_OF_MATERIALS]: IconFileSpreadsheet,
  [StockDocType.DOCUMENT_PACKAGE]: IconFileZip,
};

export type ShipmentLineCreate = {
  stockLotId: string;
  notes?: string;
  printedNotes?: string;
};

export type ShipmentCreate = {
  shipFromId?: string;
  shipToId: string;
  addressId?: string;
  carrier?: string | null;
  service?: string | null;
  deadline?: string | Date;
  notificationEmail?: string;
  shipPartial?: boolean;
  notes?: string;
  productionRun?: string | null;
  trackingNumber?: string;
  approved?: boolean;
  internalNotes?: string | null;
  team?: Team['id'];
  customProperties?: object;
};

export type ShipmentMarkAsShipped = {
  trackingNumber?: string;
};

export enum ShipmentType {
  WAREHOUSE_TO_NONWAREHOUSE = 'warehouse_to_nonwarehouse',
  NONWAREHOUSE_TO_WAREHOUSE = 'nonwarehouse_to_warehouse',
  WAREHOUSE_TO_WAREHOUSE = 'warehouse_to_warehouse',
  NONWAREHOUSE_TO_NONWAREHOUSE = 'nonwarehouse_to_nonwarehouse',
  KIT_WITHIN_WAREHOUSE = 'kit_within_warehouse',
  UNSPECIFIED = 'unspecified',
}

export const ShipmentTypeLabels: { [key: string]: string } = {
  warehouse_to_nonwarehouse: 'Warehouse → Other Address',
  nonwarehouse_to_warehouse: 'Other Address → Warehouse',
  warehouse_to_warehouse: 'Warehouse → Warehouse',
  nonwarehouse_to_nonwarehouse: 'Other Address → Other Address',
  kit_within_warehouse: 'Kit Transfer',
  unspecified: 'Unspecified',
};

export enum ShipmentStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  PICKED = 'picked',
  PACKED = 'packed',
  SHIPPED = 'shipped',
  RECEIVED = 'received',
  // eslint-disable-next-line no-restricted-syntax
  CANCELLED = 'cancelled',
  REQUESTED = 'requested',
  INGESTED = 'ingested',
  PROCESSING = 'processing',
  COMPLETE = 'complete',
  PARTIALLY_SHIPPED = 'partially_shipped',
}

export const ShipmentStatusIgnoreConflicts = [
  ShipmentStatus.COMPLETE,
  ShipmentStatus.SHIPPED,
  ShipmentStatus.RECEIVED,
];

// shipment solver types
export enum AllocationPrioritizationOptions {
  smallestFirst = 'smallest_first',
  largestFirst = 'largest_first',
  closestMatch = 'closest_match',
  oldestFirst = 'oldest_first',
  newestFirst = 'newest_first',
}

export type Supplier = {
  id: string;
  name: string;
  authenticity?: number;
  accuracy?: number;
  description?: string;
  email?: string;
  phone?: string;
  website?: string;
  certifications: string[];
  buyable?: boolean;
  purchaseOption?: string;
  scheduledReleasePeriod?: number;
  freeShip?: {
    threshold: number | null;
    shippingLead: number | null;
  };
  bestCaseLead?: number | null;
  isCustomizedInfo?: boolean;
};

export type SupplierCreate = Omit<Supplier, 'id'>;

export type SupplierLite = {
  id: string;
  name: string;
};

export type PurchaseRule = {
  id: string;
  name: string;
  org: string;
  authorizedOnly: boolean;
  requiredCertifications: string[];
  allowedSuppliers: Supplier[] | Supplier['id'][];
  preferredSuppliers: Supplier[] | Supplier['id'][];
  lastResortSuppliers: Supplier[] | Supplier['id'][];
  bannedSuppliers: Supplier[] | Supplier['id'][];
  allowedOrgSuppliers: OrgSupplier[];
  preferredOrgSuppliers: OrgSupplier[];
  lastResortOrgSuppliers: OrgSupplier[];
  bannedOrgSuppliers: OrgSupplier[];
  isDefaultPurchaseRule: string;
};

export type Printer = {
  id: string;
  name: string;
  locked: boolean;
  extPrintnodeId: string;
  externalName: string | null;
  active: boolean;
  internalNotes: string;
  externalNotes: string;
  printerType: string;
  org: Org;
  stockLocation: StockLocationLite;
  connected: boolean;
};

export enum LabelFormat {
  StockLotDefault = 'StockLotDefault',
  StockLocationDefault = 'StockLocationDefault',
  StockLocationVertical = 'StockLocationVertical',
  StockLocationShipment = 'StockLocationShipment',
  StockLocationKit = 'StockLocationKit',
  StockLocationCustomer = 'StockLocationCustomer',
  StockLocationBinSection = 'StockLocationBinSection',
  ShipmentDefault = 'ShipmentDefault',
  ShipmentLineDefault = 'ShipmentLineDefault',
  PrinterDefault = 'PrinterDefault',
  UserDefault = 'UserDefault',
  KitRequestDefault = 'KitRequestDefault',
}

export type LabelTemplate = {
  id: string;
  name: string;
  locked: boolean;
  zpl: string;
  externalName: string | null;
  active: boolean;
  internalNotes: string;
  externalNotes: string;
  labelType: string;
  printerType: PrinterLabelSize;
  labelFormat: LabelFormat | null;
  org: Org | null;
};

export type LabelRequest = {
  printer: string;
  templateName: string;
};

export type PrintLabelRequest = {
  printer: string | null;
  labelTemplate: string;
  additionalData?: Object;
  localPrinting?: boolean;
};

export type PrintStockDocumentRequest = {
  printer: string;
};

export type LabelPreviewRequest = {
  shipment: string | null;
  stockLocation: string | null;
  stockLot: string | null;
  shipmentLine: string | null;
  printer: string | null;
  additionalData: Object;
};

export type PreviewZpl = {
  zpl: string;
  labelTemplate: string;
};

export type ZplResponse = {
  zpl: string | null;
};

export type PrinterTypeSpec = {
  description: string;
  width: number;
  height: number;
};

export enum PrinterLabelSize {
  THERMAL_0201 = '0201_thermal',
  THERMAL_0302 = '0302_thermal',
  THERMAL_0402 = '0402_thermal',
  THERMAL_0406 = '0406_thermal',
  DOCUMENT_8511 = '8511_document',
}

export const printerTypeSpecs: { [key in PrinterLabelSize]: PrinterTypeSpec } =
  {
    [PrinterLabelSize.THERMAL_0201]: {
      description: '2.25in x 1.25in Thermal Transfer',
      width: 2.25,
      height: 1.25,
    },
    [PrinterLabelSize.THERMAL_0302]: {
      description: '3in x 2in Thermal Transfer',
      width: 3,
      height: 2,
    },
    [PrinterLabelSize.THERMAL_0402]: {
      description: '4in x 2in Thermal Transfer',
      width: 4,
      height: 2,
    },
    [PrinterLabelSize.THERMAL_0406]: {
      description: '4in x 6in Thermal Transfer',
      width: 4,
      height: 6,
    },
    [PrinterLabelSize.DOCUMENT_8511]: {
      description: '8.5in x 11in Document',
      width: 8.5,
      height: 11,
    },
  };

export type StockEventCreate = {
  eventType: StockEventType;
  mslFloorHours?: number;
  quant?: number;
  billable?: boolean;
  internalNotes?: string;
  externalNotes?: string;
  package?: string;
  stockLots: string[];
  stockLocation?: string;
  shipmentLine?: string | null;
};

export type PickListStockLotGroup = {
  stockLot: StockLotLite;
  shipmentLines: ShipmentLine[];
  handlingRequests: HandlingRequestLite[];
};

export type PickListTreeNode = {
  thisLocation: StockLocationLite;
  childStockLocations: PickListTreeNode[];
  stockLotGroups: PickListStockLotGroup[];
};

export type PickingFilters = {
  stockLocationId: string;
  shipmentIds: string[] | null;
  includeShipments: boolean;
  includeHandling: boolean;
  includePicked: boolean;
  handlingTypes: string[] | null;
  orgIds: string[] | null;
};

export type StockLocationPickListGETSchema = {
  stockLocationId: string;
  shipmentIds: string[] | null;
  handlingTypes: string[] | null;
  orgIds: string[] | null;
  sandbox: boolean;
};

export type HandlingRequestsGETSchema = {
  stockLotId?: StockLotCRUDSchema['id'];
  facilityId?: StockLocation['id'];
  sandbox?: boolean;
  requestTypes?: HandlingRequestType[];
};

export const pickTypeMap: { [key: string]: string } = {
  [HandlingRequestType.MSL_RESET]: HandlingRequestType.MSL_RESET,
  [HandlingRequestType.SPLICE]: HandlingRequestType.COMBINE,
  [HandlingRequestType.COMBINE]: HandlingRequestType.COMBINE,
  [HandlingRequestType.REEL]: HandlingRequestType.REEL,
  [HandlingRequestType.ANTICOUNTERFEITING]: 'inspection',
  [HandlingRequestType.SPLIT]: HandlingRequestType.SPLIT,
  [HandlingRequestType.TRANSFER]: HandlingRequestType.TRANSFER,
  [HandlingRequestType.CHECK_QUANT]: HandlingRequestType.CHECK_QUANT,
  [HandlingRequestType.DISPOSE]: HandlingRequestType.DISPOSE,
  [HandlingRequestType.REPACKAGE]: HandlingRequestType.REEL,
  [HandlingRequestType.INSPECTION_PHOTO]: 'inspection',
  [HandlingRequestType.INSPECTION_PHOTO_SAMPLE]: 'inspection',
  [HandlingRequestType.INSPECTION_PHOTO_FULL]: 'inspection',
  [HandlingRequestType.XRAY]: 'inspection',
  [HandlingRequestType.X_RAY_SAMPLE]: 'inspection',
  [HandlingRequestType.X_RAY_FULL]: 'inspection',
  [HandlingRequestType.PACKAGE_PHOTO]: HandlingRequestType.PACKAGE_PHOTO,
  [HandlingRequestType.INSPECTION_DIMENSIONAL]: 'inspection',
  [HandlingRequestType.INSPECTION_LCR]: 'inspection',
  [HandlingRequestType.ANTICOUNTERFEITING_SAMPLE]: 'inspection',
  [HandlingRequestType.ANTICOUNTERFEITING_FULL]: 'inspection',
  [HandlingRequestType.INSPECTION_SURFACE]: 'inspection',
  [HandlingRequestType.INSPECTION_DECAP]: 'inspection',
  [HandlingRequestType.INSPECTION_3RD_PARTY]: 'inspection',
  [HandlingRequestType.SHIP]: HandlingRequestType.SHIP,
  [HandlingRequestType.KIT]: HandlingRequestType.KIT,
};

export const MslValueOptions = ['1', '2', '2A', '3', '4', '5', '5A', '6'];

export type SupplierRequest = {
  autocomplete?: string | null;
  ids?: string;
  query?: string;
};

export const packageValueOptions = [
  { value: 'tube', label: 'Tube' },
  { value: 'tray', label: 'Tray' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'tape_single', label: 'Cut Tape (Single)' },
  { value: 'tape_multiple', label: 'Cut Tape (Multiple)' },
  { value: 'reel', label: 'Reel' },
];

export const packageTypeOptions = [
  { value: 'adjust_quant', label: 'Quantity Adjustment' },
  { value: 'receive', label: 'Receive' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'split', label: 'Split' },
  { value: 'dispose', label: 'Trash' },
  { value: 'ship', label: 'Shipment' },
  { value: 'user_create', label: 'Created By User' },
  { value: 'splice', label: 'Splice' },
  { value: 'combine', label: 'Combine' },
  { value: 'consume', label: 'Consumed by Production' },
];

export const tapeSizeOptionsNumeric = [
  8, 12, 16, 24, 32, 44, 56, 72, 88, 104, 120,
];

export const tapeSizeOptions = [
  '-',
  '8mm',
  '12mm',
  '16mm',
  '24mm',
  '32mm',
  '44mm',
  '56mm',
];

// We a server side version of this because client side doesn't support using just numbers as values

export const mountTypeOptions = ['SMT', 'THT', 'Other'];

export type DocumentTemplate = {
  id: string;
  name: string;
  externalName: string | null;
  active: boolean;
  default: boolean;
  internalNotes: string;
  externalNotes: string;
  org: Org | null;
  templateId: string;
  documentType: StockDocType;
};

export enum StockLotStatus {
  EXPECTED = 'expected',
  ON_HAND = 'on_hand',
  QUARANTINED = 'quarantined',
  SMT_NO_PNP = 'smt_no_pnp',
  NO_STOCK = 'no_stock',
  PROCESSING = 'processing',
}

export interface RelatedDocumentRecord {
  id: string;
  name: string;
}
